// 038 · T025/T026 — Fidelización (Prikly Pass): panel de caja e inscripción.
//
// Consume 3 endpoints ya existentes en el backend (ver
// specs/038-fidelizacion-prikly-pass/contracts/loyalty-api.md, secciones
// "Panel de caja" y "Miembros"):
//   GET  /loyalty/pos/member-panel?customerId=&saleBoxId=
//   GET  /loyalty/customers/:customerId/eligible-programs
//   POST /loyalty/members
//
// OJO: este archivo es EXCLUSIVO de este agente (panel + inscripción). El
// resto del CRUD de programas/reglas/recompensas vive en
// services/programsService.ts (otro agente) — no se toca desde aquí.

import axiosInstance from "@Services/axiosInstance";
import { API_V2 } from "@Models/GWDomain";

/** UUID del módulo `LOYALTY_VIEW` (ver require-module.decorator.ts backend). */
export const LOYALTY_VIEW_UUID = "f414e1d8-ca35-466d-bbd3-0cc53c6a7504";
/** UUID del módulo `LOYALTY_MANAGE`: inscribir, registrar visitas, suspender (escrituras). */
export const LOYALTY_MANAGE_UUID = "9e084f2a-9477-4fe0-a0e8-225008dae2ec";
/** UUID del módulo `LOYALTY_ADJUST`: ajustes manuales y su bandeja. */
export const LOYALTY_ADJUST_UUID = "1f1bf380-a586-4834-8e66-8ce402439456";

// ───────────────────────────── panel de caja ─────────────────────────────

export interface PanelCounterView {
  name: string;
  value: number;
  goal: number | null;
  cyclesCompleted: number;
}

export interface PanelRewardView {
  rewardId: number;
  name: string;
  /** 039: 'reserved' solo con reserva VIGENTE (estado 2 + reservaexpira > NOW() en la BD). */
  state: "available" | "reserved";
  expiresAt: string | null;
  /** 039 (T024) — tipo CONGELADO al emitir (1 % · 2 fijo · 3/4 gratis · 5 libre). */
  type: number | null;
  /** Valor congelado (el % o los soles). */
  value: number | null;
  /** Matriz §8.1, congelada al emitir. */
  isCombinable: boolean;
  /** Frase legible («Solo servicios de Coloración») o null si aplica a todo. */
  redemptionConditions: string | null;
  /** Reserva vigente tomada por LA CAJA del query (saleBoxId). */
  reservedByThisBox: boolean;
  /** Reserva vigente de OTRA caja: el panel deshabilita «Aplicar». */
  reservedElsewhere: boolean;
}

/** 039 (T024) — Beneficio permanente vigente del programa (Teen Card −15%). */
export interface PanelBenefitView {
  benefitId: number;
  name: string;
  /** 1 = descuento porcentual (único tipo en esta fase). */
  type: number;
  value: number;
  isCombinable: boolean;
}

/**
 * 039 (T035) — Premio del catálogo CANJEABLE con puntos desde la caja.
 * Mismo criterio que el exchange del servidor: def activa con costo > 0.
 */
export interface PanelRedeemableView {
  rewardDefId: number;
  name: string;
  /** 1 % · 2 fijo · 3 servicio gratis · 4 producto gratis · 5 premio libre. */
  type: number;
  value: number | null;
  pointsCost: number;
}

export type MembershipState =
  | "active"
  | "suspended"
  | "cancelled"
  | "expired"
  | "closed"
  | "pending"
  | "unverified";

import type { MemberTierView } from "./tiersService";

export interface PanelMembershipView {
  membershipId: number;
  programName: string;
  memberCode: string;
  state: MembershipState;
  points: number;
  unitPlural: string;
  counters: PanelCounterView[];
  rewards: PanelRewardView[];
  /** 046: solo viaja si el programa usa niveles. */
  tier?: MemberTierView;
  /** 039: beneficios permanentes vigentes del programa. SOLO viaja con la redención encendida. */
  benefits?: PanelBenefitView[];
  /** 039 (T035): catálogo canjeable con puntos. SOLO viaja con la redención encendida. */
  redeemables?: PanelRedeemableView[];
  /**
   * Fase 1 era `true` fijo (FR-070). 039: dinámico — `!LOYALTY_REDEMPTION_ENABLED`.
   * Con `true` el panel queda EXACTAMENTE como fase 1 (solo consulta).
   */
  readOnly: boolean;
}

export interface MemberPanelResponse {
  enabled: boolean;
  memberships: PanelMembershipView[];
  /**
   * Hallazgos 14a/14f (aditivo): programas activos de la sede en los que el
   * cliente todavía NO es miembro. 0 con el módulo apagado, sin add-on, en
   * solo canje o en una sede sin programas. Backend previo: ausente ⇒ 0.
   */
  enrollablePrograms?: number;
}

/**
 * Trae el panel de fidelización de un cliente para la caja. Respeta el
 * `signal` para que el hook pueda abortar a los 300 ms (SC-002) — nunca
 * reintenta por su cuenta.
 */
export async function fetchMemberPanel(
  customerId: number,
  saleBoxId: number | null,
  signal: AbortSignal
): Promise<MemberPanelResponse> {
  const params: Record<string, number> = { customerId };
  if (saleBoxId) params.saleBoxId = saleBoxId;

  const response = await axiosInstance.get(`${API_V2}/loyalty/pos/member-panel`, {
    params,
    signal,
  });
  return response.data?.data ?? { enabled: false, memberships: [] };
}

/**
 * Estado del kill-switch del módulo (`GET /loyalty/status`, JWT sin RBAC).
 * Lo consume `useLoyaltyEnabled` para ocultar el ítem del menú y la ruta
 * cuando `LOYALTY_ENABLED=false` (desvío aceptado de T006, cerrado).
 * 039: `redemptionEnabled` (aditivo) le dice al POS si montar la UI de
 * redención; el menú no cambia con él.
 */
/** Prueba gratis de 30 días de Prikly Pass (una por empresa, 2026-09-28). */
export interface LoyaltyTrial {
  status: "none" | "pending" | "active" | "expired";
  startedAt: string | null;
  endsAt: string | null;
  daysLeft: number;
  durationDays: number;
  canStartTrial: boolean;
}

export interface LoyaltyStatus {
  enabled: boolean;
  redemptionEnabled: boolean;
  /** Uso pleno: add-on vigente, empresa exenta o prueba gratis ACTIVA. */
  addonActive: boolean;
  /** Contratado de verdad (o exento): distingue "en prueba" de "activo". */
  hasAddon: boolean;
  /**
   * Solo canje: prueba gratis vencida, o add-on pagado dado de baja/vencido
   * con premios ya ganados. La caja sigue canjeando esos premios; no se
   * acumula ni se gestionan programas.
   */
  redeemOnly: boolean;
  /** Por qué quedó en solo canje (texto de la pantalla). */
  redeemOnlyReason: LoyaltyRedeemOnlyReason | null;
  trial: LoyaltyTrial | null;
}

export type LoyaltyRedeemOnlyReason = "trial_expired" | "addon_ended";

export async function fetchLoyaltyStatus(): Promise<LoyaltyStatus> {
  const response = await axiosInstance.get(`${API_V2}/loyalty/status`);
  const data = response.data?.data ?? {};
  const t = data.trial;
  return {
    enabled: !!data.enabled,
    redemptionEnabled: !!data.redemptionEnabled,
    addonActive: !!data.addonActive,
    // Backend previo al 2026-09-28 no manda hasAddon: addonActive era "contratado".
    hasAddon: data.hasAddon === undefined ? !!data.addonActive : !!data.hasAddon,
    redeemOnly: !!data.redeemOnly,
    redeemOnlyReason:
      data.redeemOnlyReason === "addon_ended" || data.redeemOnlyReason === "trial_expired"
        ? data.redeemOnlyReason
        : null,
    trial: t
      ? {
          status: t.status ?? "none",
          startedAt: t.startedAt ?? null,
          endsAt: t.endsAt ?? null,
          daysLeft: Number(t.daysLeft ?? 0),
          durationDays: Number(t.durationDays ?? 30),
          // Spec 044 (F-R2-12): sin prueba si la empresa ya paga en otra sede.
          canStartTrial: !!t.canStartTrial && data.paidInOtherSites !== true && t.paidInOtherSites !== true,
        }
      : null,
  };
}

// ───────────────────── reserva de premios (039 · T011) ─────────────────────

/**
 * Respuesta de `POST /loyalty/pos/rewards/:rewardId/reserve`.
 * `frozenVigency` es la vigencia del premio CONGELADA al reservar
 * (clarificación 2026-08-23): el cobro valida contra ella, no contra el reloj
 * del momento de pagar.
 */
export interface ReserveRewardResponse {
  reserved: true;
  /** Vencimiento de la reserva (lo puso NOW() de la BD). */
  expiresAt: string | null;
  ttlSeconds: number;
  frozenVigency: string | null;
}

/**
 * Toma O RENUEVA (idempotente por caja) la reserva exclusiva de un premio.
 * La renovación por heartbeat y el ciclo de vida viven en el hook
 * `useRewardReservation` (T019) — este servicio solo pega al endpoint.
 * Errores tipificados: ver `getReservationErrorMessage`.
 */
export async function reserveReward_service(
  rewardId: number,
  saleBoxId: number
): Promise<ReserveRewardResponse> {
  const response = await axiosInstance.post(
    `${API_V2}/loyalty/pos/rewards/${rewardId}/reserve`,
    { saleBoxId }
  );
  return response.data?.data;
}

/**
 * Libera la reserva (idempotente: liberar un premio ya libre responde OK).
 * El release por `fetch keepalive` del beforeunload es del hook T019 — acá
 * se usa el axiosInstance normal, como el resto del archivo.
 */
export async function releaseReward_service(
  rewardId: number,
  saleBoxId: number
): Promise<{ released: true }> {
  const response = await axiosInstance.post(
    `${API_V2}/loyalty/pos/rewards/${rewardId}/release`,
    { saleBoxId }
  );
  return response.data?.data;
}

// ───────────────── preview de beneficios en el cobro (039 · T017) ─────────────────

/** Línea del carrito tal como viaja al preview (ids y cantidades, nunca montos del beneficio). */
export interface PreviewBenefitItemPayload {
  /** Ref de la línea (eco en la respuesta; el POS usa el índice del carrito). */
  ref: string | number;
  priceServiceId?: number;
  localProductId?: number;
  quantity: number;
  /** Contexto §8.1: promoción YA confirmada en la línea (para detectar conflictos). */
  promotionId?: number;
  /** Contexto: descuento por unidad YA aplicado (nombre histórico de `discount`). */
  existingUnitDiscount?: number;
  // ── Fix 2026-09-29: los MISMOS modificadores de línea que recibe el cobro,
  // para que el server arme el carrito con el mismo bloque de precios.
  /** Descuento manual por unidad en soles (igual que items[].discount del cobro). */
  discount?: number;
  /** Recargo por unidad en soles (igual que items[].surcharge del cobro). */
  surcharge?: number;
  isCourtesy?: boolean;
  isCollaboratorPrice?: boolean;
  withdrawalDetailId?: number;
  /** Beneficios permanentes quitados SOLO en esta línea. */
  suppressedBenefitIds?: number[];
}

export interface PreviewLoyaltyBenefitsPayload {
  customerId: number;
  saleBoxId: number;
  items: PreviewBenefitItemPayload[];
  appliedRewardIds: number[];
  suppressedBenefitIds: number[];
  /** Promociones confirmadas en el modal de cobro (mismo campo que el cobro). */
  appliedPromotionIds?: number[];
  /** Cupón de la venta (mismo campo que el cobro). */
  couponCode?: string;
}

export interface PreviewBenefitLine {
  ref: string | number;
  rewardId?: number;
  benefitId?: number;
  /** Lo que pinta la sub-línea del ticket («Canje: Corte gratis», «Teen Card −15%»). */
  benefitName: string;
  unitDiscount: number;
  isFree: boolean;
}

export interface PreviewLoyaltyBenefitsResponse {
  lines: PreviewBenefitLine[];
  /**
   * Total del carrito EVALUADO tras beneficios. OJO: el preview no ve ítems
   * manuales, giftcards ni paquetes, así que NO es el total de la venta.
   */
  newTotal: number;
  /**
   * Cuánto restan los beneficios al carrito evaluado (calculado por el server,
   * al céntimo). La caja lo resta de SU total local — Resumen, modal de cobro
   * y guard de cobertura — sin recalcular nada.
   */
  discountTotal?: number;
  appliedBenefits: Array<{ benefitId: number; name: string; value: number }>;
  /** Conflictos §8.1 — acá NO son error HTTP: la caja los muestra y la cajera decide. */
  conflicts: Array<{
    ref: string | number;
    code: string;
    message: string;
    /** Con qué chocó: 'promotion' | 'coupon' | 'discount' | 'reward' | 'benefit'. */
    conflictWith?: string;
    benefitId?: number;
    rewardId?: number;
    /** Nombre del beneficio/premio que no se aplicó. */
    name?: string;
    promotionId?: number | null;
    couponId?: number | null;
  }>;
}

/**
 * `POST /pos/loyalty/preview-benefits` — SIN efectos. El backend invoca el
 * MISMO motor puro del cobro (paridad preview↔cobro al céntimo, SC-003); el
 * front pinta este resultado y JAMÁS recalcula montos (research D17.3).
 * Con el kill-switch de redención apagado la ruta responde 404.
 */
export async function previewLoyaltyBenefits_service(
  payload: PreviewLoyaltyBenefitsPayload,
  options?: { signal?: AbortSignal }
): Promise<PreviewLoyaltyBenefitsResponse> {
  const response = await axiosInstance.post(
    `${API_V2}/pos/loyalty/preview-benefits`,
    payload,
    options?.signal ? { signal: options.signal } : undefined
  );
  return response.data?.data;
}

// ────────── canje desde caja y verificación (039 · T034/T036) ──────────

/** Respuesta del canje: MISMO shape que el exchange del CRM (delegación pura). */
export interface ExchangedReward {
  rewardId: number;
  rewardDefId: number;
  membershipId: number;
  name: string;
  pointsPaid: number;
  /** Saldo de la membresía DESPUÉS del canje (para pintar sin refetch). */
  pointsBalance: number;
  expiresAt: string | null;
  state: "available";
}

/**
 * Canjea puntos por un premio del catálogo desde la caja
 * (`POST /loyalty/pos/exchange`). OJO (clarificación 2026-08-23): el canje
 * queda FIRME aunque el cobro luego se cancele — los puntos ya se
 * descontaron y el premio queda a nombre de la membresía. No hay reversa.
 * Errores: ver `getExchangeErrorMessage`.
 */
export async function exchangeFromPos_service(
  membershipId: number,
  rewardDefId: number
): Promise<ExchangedReward> {
  const response = await axiosInstance.post(`${API_V2}/loyalty/pos/exchange`, {
    membershipId,
    rewardDefId,
  });
  return response.data?.data;
}

/**
 * Verificación de identidad en caja (`POST /loyalty/members/:id/verify`,
 * PRD §14.2): documento a la vista, transición 7→1. Vale con la redención
 * apagada (research D13).
 */
export async function verifyMembership_service(
  membershipId: number,
  note?: string
): Promise<{ membershipId: number; state: "active"; verifiedAt: string }> {
  const response = await axiosInstance.post(
    `${API_V2}/loyalty/members/${membershipId}/verify`,
    note ? { note } : {}
  );
  return response.data?.data;
}

// ───────────────────────────── inscripción ─────────────────────────────

export interface EligibleProgram {
  programId: number;
  name: string;
  displayName: string;
  description: string | null;
  unitPlural: string;
  terms: string | null;
  /** Ya tiene membresía activa de este programa. */
  alreadyMember: boolean;
  membershipId: number | null;
  /** Cumple la elegibilidad configurada. */
  eligible: boolean;
  ineligibleField: string | null;
  /** Frase legible del motivo, lista para mostrar. */
  ineligibleReason: string | null;
}

/** Programas en los que se puede inscribir a un cliente. Se pide solo al abrir el modal. */
export async function fetchEligiblePrograms(customerId: number): Promise<EligibleProgram[]> {
  const response = await axiosInstance.get(
    `${API_V2}/loyalty/customers/${customerId}/eligible-programs`
  );
  return response.data?.data ?? [];
}

export interface EnrollConsent {
  /** Tratamiento de datos. Obligatorio — sin él el backend responde CONSENT_REQUIRED. */
  data: boolean;
  /** Marketing: separado y opcional, nace desmarcado (§15). */
  marketing?: boolean;
  termsVersion?: string;
}

export interface EnrollPayload {
  programId: number;
  customerId: number;
  consent: EnrollConsent;
  /** 1 = caja, 2 = CRM. Por defecto 2 en el backend; la caja siempre manda 1. */
  origin?: 1 | 2;
}

export interface MembershipView {
  membershipId: number;
  programId: number;
  programName: string;
  customerId: number;
  memberCode: string;
  state: MembershipState;
  points: number;
  accumulatedSpend: number;
  enrolledAt: string;
  enrollmentOrigin: number;
  enrollmentLocalId: number | null;
  enrollmentEmployeeId: number | null;
  verificationMethod: number | null;
  verifiedAt: string | null;
  dataConsent: boolean;
  marketingConsent: boolean;
  termsVersion: string | null;
  reactivated?: boolean;
}

export async function enrollMember(payload: EnrollPayload): Promise<MembershipView> {
  const response = await axiosInstance.post(`${API_V2}/loyalty/members`, payload);
  return response.data?.data;
}

// ───────────────────────────── errores tipificados ─────────────────────────────

/**
 * Mensajes de respaldo por código de error. El backend YA manda un `message`
 * en español listo para mostrar (ver loyalty-membership.service.ts), así que
 * esto solo entra en juego si por algún motivo no llega.
 */
const LOYALTY_ERROR_FALLBACKS: Record<string, string> = {
  NOT_ELIGIBLE: "El cliente no cumple los requisitos de elegibilidad de este programa.",
  ALREADY_MEMBER: "El cliente ya tiene una membresía activa de este programa.",
  PROGRAM_NOT_ACTIVE: "Este programa no está activo en este momento.",
  LOCAL_NOT_ELIGIBLE: "Esta sede no participa de este programa.",
  CONSENT_REQUIRED: "Falta el consentimiento de tratamiento de datos.",
  // Los lanza `loadCustomer`/`loadProgramForEnroll` en enroll y eligible-programs.
  CUSTOMER_NOT_FOUND: "El cliente no existe.",
  PROGRAM_NOT_FOUND: "El programa de fidelización no existe.",
};

/**
 * 039 · T011 — Respaldos de la RESERVA de premios (redemption-api.md §1),
 * patrón `ADJUSTMENT_ERROR_FALLBACKS`. Los consumen el hook
 * `useRewardReservation` y el panel de caja (tareas posteriores) vía
 * `getReservationErrorMessage`.
 */
export const RESERVATION_ERROR_FALLBACKS: Record<string, string> = {
  REWARD_NOT_FOUND: "El premio no existe.",
  REWARD_RESERVED_ELSEWHERE: "El premio está reservado por otra caja en este momento.",
  REWARD_NOT_AVAILABLE: "El premio ya no está disponible para redimir.",
  REWARD_EXPIRED: "El premio está vencido: ya no se puede redimir.",
  MEMBERSHIP_UNVERIFIED:
    "La membresía está vinculada pero no verificada: verifica la identidad del cliente antes de redimir.",
  MEMBERSHIP_NOT_ACTIVE: "La membresía no está activa: no puede redimir premios.",
  PROGRAM_NOT_REDEEMING: "El programa no está redimiendo premios en este momento.",
  LOCAL_NOT_ELIGIBLE: "El programa no opera en la sede de esta caja.",
  // 039 · T017: el cobro (y solo el cobro) responde 400 con este código si
  // llegan premios con el kill-switch de redención apagado.
  REDEMPTION_DISABLED:
    "La redención de beneficios está desactivada. Quita los premios del carrito para cobrar.",
};

/** Extrae un mensaje de error legible en español desde una respuesta de axios. */
export function getLoyaltyErrorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { code?: string; message?: unknown } } })
    ?.response;
  const code = response?.data?.code;
  const rawMessage = response?.data?.message;
  const message = typeof rawMessage === "string" ? rawMessage : undefined;

  return (
    message ||
    (code && LOYALTY_ERROR_FALLBACKS[code]) ||
    "No se pudo completar la operación. Intenta nuevamente."
  );
}

/** Código de error crudo, para que el hook/panel distingan los 409 de reserva. */
export function getLoyaltyErrorCode(error: unknown): string | undefined {
  return (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
}

/**
 * 039 · T034/T036 — Respaldos del CANJE desde caja y la verificación
 * (redemption-api.md §4/§5), patrón ADJUSTMENT_ERROR_FALLBACKS.
 */
export const EXCHANGE_ERROR_FALLBACKS: Record<string, string> = {
  INSUFFICIENT_POINTS: "El cliente no tiene puntos suficientes para este canje.",
  REWARD_NOT_EXCHANGEABLE: "Este premio no se canjea con puntos.",
  REWARD_NOT_REDEEMABLE: "Este premio no está activo en el catálogo.",
  REWARD_DEF_NOT_FOUND: "El premio no existe en el catálogo del programa.",
  MEMBERSHIP_NOT_FOUND: "La membresía no existe.",
  MEMBERSHIP_NOT_ACTIVE: "La membresía no está activa: no puede canjear premios.",
  MEMBERSHIP_UNVERIFIED:
    "La membresía está vinculada pero no verificada: verifica la identidad del cliente antes de canjear.",
  MEMBERSHIP_NOT_UNVERIFIED: "La membresía ya estaba verificada.",
};

/**
 * Mensaje legible para los errores del canje. Con `INSUFFICIENT_POINTS` el
 * backend manda `{required, available}` en el body: si vienen, se compone la
 * frase con los números (más accionable que el mensaje genérico).
 */
export function getExchangeErrorMessage(error: unknown): string {
  const response = (
    error as {
      response?: {
        data?: { code?: string; message?: unknown; required?: unknown; available?: unknown };
      };
    }
  )?.response;
  const code = response?.data?.code;

  if (code === "INSUFFICIENT_POINTS") {
    const required = Number(response?.data?.required);
    const available = Number(response?.data?.available);
    if (Number.isFinite(required) && Number.isFinite(available)) {
      const missing = Math.max(0, required - available);
      return `Faltan ${missing} puntos: necesita ${required}, tiene ${available}.`;
    }
  }

  const rawMessage = response?.data?.message;
  const message = typeof rawMessage === "string" ? rawMessage : undefined;

  return (
    message ||
    (code && EXCHANGE_ERROR_FALLBACKS[code]) ||
    "No se pudo canjear el premio. Intenta nuevamente."
  );
}

/**
 * Mensaje legible para los errores de reserva/liberación (039 · T011).
 * Con `REWARD_RESERVED_ELSEWHERE` el backend manda `saleBoxName` en el body:
 * si viene, se nombra la caja que tiene la reserva (más accionable que el
 * mensaje genérico del servidor).
 */
export function getReservationErrorMessage(error: unknown): string {
  const response = (
    error as {
      response?: { data?: { code?: string; message?: unknown; saleBoxName?: unknown } };
    }
  )?.response;
  const code = response?.data?.code;
  const saleBoxName = response?.data?.saleBoxName;

  if (code === "REWARD_RESERVED_ELSEWHERE" && typeof saleBoxName === "string" && saleBoxName) {
    return `El premio está reservado por la caja «${saleBoxName}» en este momento.`;
  }

  const rawMessage = response?.data?.message;
  const message = typeof rawMessage === "string" ? rawMessage : undefined;

  return (
    message ||
    (code && RESERVATION_ERROR_FALLBACKS[code]) ||
    "No se pudo reservar el premio. Intenta nuevamente."
  );
}
